/**
 * Supabase Learned Locations Service
 * Manages learned locations using Supabase database
 */

import { supabase } from './customSupabaseClient.js';

class SupabaseLearnedLocationsService {
  constructor() {
    this.tableName = 'learned_locations';
    this.isInitialized = false;
  }

  /**
   * Initialize the service
   */
  async init() {
    if (this.isInitialized) return;
    
    try {
      // Test database connection with a simple query
      const { data, error } = await supabase
        .from(this.tableName)
        .select('id')
        .limit(1);
      
      if (error) {
        console.warn('Supabase learned locations service not available:', error.message);
        this.isInitialized = false;
        return;
      }
      
      this.isInitialized = true;
      // Service initialized
    } catch (error) {
      console.warn('Failed to initialize Supabase learned locations service:', error);
      this.isInitialized = false;
    }
  }

  /**
   * Search learned locations with proper error handling and input sanitization
   * @param {string} query - Search query
   * @param {number} maxResults - Maximum number of results
   * @returns {Promise<Array>} Array of learned locations
   */
  async searchLearnedLocations(query, maxResults = 8) {
    await this.init();
    
    if (!this.isInitialized) {
      return [];
    }

    try {
      // Validate and sanitize input
      const sanitizedQuery = this.sanitizeSearchQuery(query);
      if (!sanitizedQuery || sanitizedQuery.length < 2) {
        return [];
      }

      // Use proper Supabase query with error handling
      const { data, error } = await supabase
        .from(this.tableName)
        .select('*')
        .ilike('search_key', `%${sanitizedQuery}%`)
        .eq('is_active', true)
        .order('search_count', { ascending: false })
        .order('last_searched_at', { ascending: false })
        .limit(maxResults);

      if (error) {
        // Handle specific error types
        if (error.code === 'PGRST116' || error.status === 406) {
          console.warn('Learned locations table not accessible or RLS policy issue:', error.message);
          return [];
        }
        
        if (error.code === 'PGRST301') {
          console.warn('No rows found in learned locations table');
          return [];
        }
        
        console.error('Error searching learned locations:', {
          message: error.message,
          code: error.code,
          status: error.status,
          details: error.details
        });
        return [];
      }

      // Update search count for found results
      if (data && data.length > 0) {
        this.updateSearchCounts(data.map(item => item.id));
      }

      return data || [];
    } catch (error) {
      console.error('Unexpected error in searchLearnedLocations:', {
        message: error.message,
        stack: error.stack,
        query: query
      });
      return [];
    }
  }

  /**
   * Sanitize search query to prevent injection and encoding issues
   * @param {string} query - Raw search query
   * @returns {string} Sanitized query
   */
  sanitizeSearchQuery(query) {
    if (!query || typeof query !== 'string') {
      return '';
    }

    // Remove potentially dangerous characters and normalize
    return query
      .trim()
      .toLowerCase()
      .replace(/[<>'"&]/g, '') // Remove HTML/XML characters
      .replace(/[;()]/g, '') // Remove SQL injection characters
      .replace(/\s+/g, ' ') // Normalize whitespace
      .substring(0, 100); // Limit length
  }

  /**
   * Save learned location
   * @param {Object} locationData - Location data to save
   * @returns {Promise<boolean>} Success status
   */
  async saveLearnedLocation(locationData) {
    await this.init();
    
    if (!this.isInitialized) {
      return false;
    }

    try {
      const searchKey = locationData.searchKey.toLowerCase().trim();
      
      // Check if location already exists
      const { data: existing, error: searchError } = await supabase
        .from(this.tableName)
        .select('*')
        .eq('search_key', searchKey)
        .maybeSingle();

      if (searchError && searchError.code !== 'PGRST116') {
        // PGRST116 is "not found" error, which is expected for new locations
        if ((typeof import.meta !== 'undefined' && import.meta.env?.DEV)) {
          console.error('Error checking existing location:', searchError);
        }
        return false;
      }

      if (existing) {
        const updatePayload = {
          search_count: existing.search_count + 1,
          last_searched_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        if (locationData.source === 'google') {
          const latDiff = Math.abs(parseFloat(existing.latitude) - parseFloat(locationData.latitude));
          const lngDiff = Math.abs(parseFloat(existing.longitude) - parseFloat(locationData.longitude));
          const addressChanged = (existing.address || '').trim() !== (locationData.address || '').trim();

          if (latDiff > 0.0005 || lngDiff > 0.0005 || addressChanged) {
            updatePayload.latitude = locationData.latitude;
            updatePayload.longitude = locationData.longitude;
            updatePayload.address = locationData.address;
            updatePayload.name = locationData.name || locationData.address;
            updatePayload.source = 'google';
          }
        }

        const { error: updateError } = await supabase
          .from(this.tableName)
          .update(updatePayload)
          .eq('id', existing.id);

        if (updateError) {
          if ((typeof import.meta !== 'undefined' && import.meta.env?.DEV)) {
            console.error('Error updating learned location:', updateError);
          }
          return false;
        }
      } else {
        // Insert new location
        const { error: insertError } = await supabase
          .from(this.tableName)
          .insert({
            search_key: searchKey,
            name: locationData.name,
            address: locationData.address,
            latitude: locationData.latitude,
            longitude: locationData.longitude,
            source: locationData.source || 'api',
            search_count: 1,
            is_active: true,
            created_at: new Date().toISOString(),
            last_searched_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (insertError) {
          if ((typeof import.meta !== 'undefined' && import.meta.env?.DEV)) {
            console.error('Error inserting learned location:', insertError);
          }
          return false;
        }
      }

      return true;
    } catch (error) {
      if ((typeof import.meta !== 'undefined' && import.meta.env?.DEV)) {
        console.error('Error saving learned location:', error);
      }
      return false;
    }
  }

  /**
   * Update search counts for multiple locations
   * @param {Array} locationIds - Array of location IDs
   */
  async updateSearchCounts(locationIds) {
    if (!this.isInitialized || !locationIds || locationIds.length === 0) {
      return;
    }

    try {
      // Get current search counts first
      const { data: currentData, error: fetchError } = await supabase
        .from(this.tableName)
        .select('id, search_count')
        .in('id', locationIds);

      if (fetchError) {
        if ((typeof import.meta !== 'undefined' && import.meta.env?.DEV)) {
          console.error('Error fetching current search counts:', fetchError);
        }
        return;
      }

      // Update each location individually with incremented count
      const updatePromises = currentData.map(location => 
        supabase
          .from(this.tableName)
          .update({
            search_count: (location.search_count || 0) + 1,
            last_searched_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', location.id)
      );

      const results = await Promise.all(updatePromises);
      
      // Check for errors
      const errors = results.filter(result => result.error);
      if (errors.length > 0 && (typeof import.meta !== 'undefined' && import.meta.env?.DEV)) {
        console.error('Error updating search counts:', errors[0].error);
      }
    } catch (error) {
      if ((typeof import.meta !== 'undefined' && import.meta.env?.DEV)) {
        console.error('Error updating search counts:', error);
      }
    }
  }

  /**
   * Get learned location by search key
   * @param {string} searchKey - Search key
   * @returns {Promise<Object|null>} Learned location or null
   */
  async getLearnedLocation(searchKey) {
    await this.init();
    
    if (!this.isInitialized) {
      return null;
    }

    try {
      const { data, error } = await supabase
        .from(this.tableName)
        .select('*')
        .eq('search_key', searchKey.toLowerCase().trim())
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        if (error.code !== 'PGRST116' && (typeof import.meta !== 'undefined' && import.meta.env?.DEV)) {
          console.error('Error getting learned location:', error);
        }
        return null;
      }

      return data;
    } catch (error) {
      if ((typeof import.meta !== 'undefined' && import.meta.env?.DEV)) {
        console.error('Error getting learned location:', error);
      }
      return null;
    }
  }

  /**
   * Clean up old learned locations
   * @returns {Promise<number>} Number of locations cleaned up
   */
  async cleanupOldLocations() {
    await this.init();
    
    if (!this.isInitialized) {
      return 0;
    }

    try {
      // Mark locations as inactive if they haven't been searched in 30 days
      const { data, error } = await supabase
        .rpc('cleanup_old_learned_locations');

      if (error) {
        if ((typeof import.meta !== 'undefined' && import.meta.env?.DEV)) {
          console.error('Error cleaning up old locations:', error);
        }
        return 0;
      }

      return data || 0;
    } catch (error) {
      if ((typeof import.meta !== 'undefined' && import.meta.env?.DEV)) {
        console.error('Error cleaning up old locations:', error);
      }
      return 0;
    }
  }

  /**
   * Clear all learned locations
   * @returns {Promise<boolean>} Success status
   */
  async clearAllLearnedLocations() {
    await this.init();
    
    if (!this.isInitialized) {
      return false;
    }

    try {
      const { error } = await supabase
        .from(this.tableName)
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all records

      if (error) {
        if ((typeof import.meta !== 'undefined' && import.meta.env?.DEV)) {
          console.error('Error clearing learned locations:', error);
        }
        return false;
      }

      return true;
    } catch (error) {
      if ((typeof import.meta !== 'undefined' && import.meta.env?.DEV)) {
        console.error('Error clearing learned locations:', error);
      }
      return false;
    }
  }

  /**
   * Get learned locations statistics
   * @returns {Promise<Object>} Statistics
   */
  async getLearnedLocationsStats() {
    await this.init();
    
    if (!this.isInitialized) {
      return {
        total_locations: 0,
        active_locations: 0,
        inactive_locations: 0,
        avg_search_count: 0,
        max_search_count: 0,
        locations_learned_this_week: 0,
        locations_searched_today: 0
      };
    }

    try {
      // Get basic counts
      const { count: totalCount, error: totalError } = await supabase
        .from(this.tableName)
        .select('*', { count: 'exact', head: true });

      const { count: activeCount, error: activeError } = await supabase
        .from(this.tableName)
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);

      // Get statistics
      const { data: statsData, error: statsError } = await supabase
        .from(this.tableName)
        .select('search_count, created_at, last_searched_at')
        .eq('is_active', true);

      if (totalError || activeError || statsError) {
        if ((typeof import.meta !== 'undefined' && import.meta.env?.DEV)) {
          console.error('Error getting learned locations stats:', { totalError, activeError, statsError });
        }
        return {
          total_locations: 0,
          active_locations: 0,
          inactive_locations: 0,
          avg_search_count: 0,
          max_search_count: 0,
          locations_learned_this_week: 0,
          locations_searched_today: 0
        };
      }

      const totalLocations = totalCount || 0;
      const activeLocations = activeCount || 0;
      const inactiveLocations = totalLocations - activeLocations;

      // Calculate statistics from statsData
      const now = new Date();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

      const locations = statsData || [];
      const avgSearchCount = locations.length > 0 
        ? locations.reduce((sum, l) => sum + (l.search_count || 0), 0) / locations.length 
        : 0;
      const maxSearchCount = locations.length > 0 
        ? Math.max(...locations.map(l => l.search_count || 0)) 
        : 0;
      const locationsLearnedThisWeek = locations.filter(l => 
        new Date(l.created_at) > oneWeekAgo
      ).length;
      const locationsSearchedToday = locations.filter(l => 
        new Date(l.last_searched_at) > oneDayAgo
      ).length;

      return {
        total_locations: totalLocations,
        active_locations: activeLocations,
        inactive_locations: inactiveLocations,
        avg_search_count: avgSearchCount,
        max_search_count: maxSearchCount,
        locations_learned_this_week: locationsLearnedThisWeek,
        locations_searched_today: locationsSearchedToday
      };
    } catch (error) {
      if ((typeof import.meta !== 'undefined' && import.meta.env?.DEV)) {
        console.error('Error getting learned locations stats:', error);
      }
      return {
        total_locations: 0,
        active_locations: 0,
        inactive_locations: 0,
        avg_search_count: 0,
        max_search_count: 0,
        locations_learned_this_week: 0,
        locations_searched_today: 0
      };
    }
  }
}

export default new SupabaseLearnedLocationsService();
